import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faFlagCheckered, faTrophy, faPause, faPlay,
  faForwardStep, faRobot, faHandshake, faRotateLeft,
  faArrowUp, faArrowDown, faEquals,
  faStopwatch, faMedal, faFire, faHeartPulse, faOm, faCouch, faBolt,
  faClone, faPersonSwimming, faCrown,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { Activity, DailyChallenge, PlayerStats } from '../types/activity';
import { useScrollDismiss } from '../hooks/useScrollDismiss';
import ActivityChip from '../components/ActivityChip';
import PlayerStatsTooltip from '../components/PlayerStatsTooltip';
import { getDaysFromCycleStart, isSameLocalDay } from '../utils/dateUtils';
import '../styles/SportBreakdown.css';
import '../styles/ItemReward.css';
import { getSportIcon } from '../utils/sportEmoji';
import { clampTooltipX } from '../utils/tooltipClamp';
import scorescreenImg from '../assets/scorescreen.jpg';
import logo from '../assets/logo.png';
import { generateAllComments, generateFinalComment } from '../utils/duelComments';
import { SPORT_COLORS, getSportLabel } from '../utils/sportData';
import ItemRewardModal from '../components/ItemRewardModal';

// ── VictoryBadges: Badge-Icons mit Tooltip (wie BadgeBar) ──
function VictoryBadges({ badges }: { badges: BadgeEntry[] }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [tooltipText, setTooltipText] = useState('');
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeId) { setTooltipPos(null); return; }
    const handler = () => setActiveId(null);
    const scrollHandler = () => setActiveId(null);
    document.addEventListener('click', handler, { once: true });
    window.addEventListener('scroll', scrollHandler, { once: true, passive: true });
    return () => {
      document.removeEventListener('click', handler);
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [activeId]);

  // Tooltip-Position nach Render setzen
  useEffect(() => {
    if (!activeId || !wrapRef.current) { setTooltipPos(null); return; }
    const el = wrapRef.current.querySelector(`[data-badge-id="${activeId}"]`) as HTMLElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
    }
  }, [activeId]);

  return (
    <div className="duel-victory-badges" ref={wrapRef}>
      <div className="duel-victory-badges-row">
        {badges.map((b) => {
          const id = b.badge?.code || '';
          const name = b.badge?.name || '';
          const icon = b.badge?.icon || 'faAward';
          const isOpen = activeId === id;
          const text = b.old_tier === 0
            ? `${name} — Tier ${b.new_tier} (+${b.reward} pts/week)`
            : `${name} — Tier ${b.old_tier} → ${b.new_tier} (+${b.reward} pts/week)`;
          return (
            <div
              key={id}
              data-badge-id={id}
              className="badge-icon-wrap"
              onMouseEnter={(e) => {
                if (!isTouch.current) {
                  setActiveId(id);
                  setTooltipText(text);
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
                }
              }}
              onMouseLeave={() => { if (!isTouch.current) { setActiveId(null); } }}
              onClick={(e) => {
                if (isTouch.current) {
                  e.stopPropagation();
                  if (isOpen) { setActiveId(null); } else {
                    setActiveId(id);
                    setTooltipText(text);
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
                  }
                }
              }}
              style={{ cursor: 'default' }}
            >
              <div className="badge-icon" style={{
                border: '2px solid #e6b800',
                background: 'rgba(230,184,0,0.15)',
                color: '#e6b800',
              }}>
                <FontAwesomeIcon icon={getSportIcon(icon)} />
              </div>
              <span className="item-tier-icons" style={{ color: '#81c784', textAlign: 'center', width: '100%' }}>
                {'▲'.repeat(b.new_tier)}
              </span>
            </div>
          );
        })}
      </div>
      {tooltipPos && ReactDOM.createPortal(
        <div className="badge-tooltip" style={{ position: 'fixed', top: tooltipPos.y, left: tooltipPos.x, transform: 'translateX(-50%) translateY(-100%)', zIndex: 9999, pointerEvents: 'none' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#e6b800', whiteSpace: 'nowrap' }}>{tooltipText}</div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Types ────────────────────────────────────────────────────────────────────

interface PlayerSide {
  id: number;
  display_name: string;
  avatar_url: string;
  level_value: number;
  is_bot: boolean;
  activities: Activity[];
  total_points: number;
  rank?: number;
  elo_rating?: number;
  consolation_points?: number;
}

interface DuelData {
  cycle: { id: number; week_label: string; start_at_utc: string; workout_deadline_utc: string };
  me: PlayerSide;
  opponent: PlayerSide;
  winner: 'me' | 'opponent' | 'draw';
  elo?: { me: { before: number | null; after: number | null; change: number | null }; opp: { before: number | null; after: number | null; change: number | null } };
  bet?: { net_points: number; won: number; lost: number; draw: number } | null;
  badges?: {
    me: { new: BadgeEntry[]; upgraded: BadgeEntry[]; unchanged: any[] };
    opp: { new: BadgeEntry[]; upgraded: BadgeEntry[]; unchanged: any[] };
  };
}

interface BadgeEntry {
  badge: { code: string; name: string; icon: string; description: string };
  old_tier: number;
  new_tier: number;
  reward: number;
}

interface WorkoutStep {
  dayIndex: number;
  workout: Activity;
  player: 'me' | 'opp';
  comment: string;
  mePointsAfter: number;
  oppPointsAfter: number;
  meChallengeAfter: number;
  oppChallengeAfter: number;
  mePrAfter: number;
  oppPrAfter: number;
}

type Phase = 'loading' | 'error' | 'ready' | 'playing' | 'paused' | 'victory';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const STEP_MS_BASE = 3500;

// ── Helpers ──────────────────────────────────────────────────────────────────

function buildSteps(
  meActs: Activity[],
  oppActs: Activity[],
  cycleStart: string,
  comments: Record<string, string>,
): WorkoutStep[] {
  const days = getDaysFromCycleStart(cycleStart);
  const meDays: Activity[][] = Array.from({ length: 7 }, () => []);
  const oppDays: Activity[][] = Array.from({ length: 7 }, () => []);

  for (const a of meActs) {
    const d = days.findIndex(day => isSameLocalDay(a.started_at_utc, day));
    if (d >= 0) meDays[d].push(a);
  }
  for (const a of oppActs) {
    const d = days.findIndex(day => isSameLocalDay(a.started_at_utc, day));
    if (d >= 0) oppDays[d].push(a);
  }

  const result: WorkoutStep[] = [];
  let meRunning = 0, oppRunning = 0;
  let meChalRunning = 0, oppChalRunning = 0;
  let mePrRunning = 0, oppPrRunning = 0;

  for (let d = 0; d < 7; d++) {
    const me = meDays[d];
    const opp = oppDays[d];
    for (let i = 0; i < Math.max(me.length, opp.length); i++) {
      if (i < me.length) {
        meRunning    += Number(me[i].total_points)            || 0;
        meChalRunning += Number(me[i].challenge_bonus_points) || 0;
        mePrRunning   += Number(me[i].pr_bonus_points)        || 0;
        result.push({ dayIndex: d, workout: me[i], player: 'me',  comment: comments[String(me[i].id)]  ?? '', mePointsAfter: meRunning, oppPointsAfter: oppRunning, meChallengeAfter: meChalRunning, oppChallengeAfter: oppChalRunning, mePrAfter: mePrRunning, oppPrAfter: oppPrRunning });
      }
      if (i < opp.length) {
        oppRunning    += Number(opp[i].total_points)            || 0;
        oppChalRunning += Number(opp[i].challenge_bonus_points) || 0;
        oppPrRunning   += Number(opp[i].pr_bonus_points)        || 0;
        result.push({ dayIndex: d, workout: opp[i], player: 'opp', comment: comments[String(opp[i].id)] ?? '', mePointsAfter: meRunning, oppPointsAfter: oppRunning, meChallengeAfter: meChalRunning, oppChallengeAfter: oppChalRunning, mePrAfter: mePrRunning, oppPrAfter: oppPrRunning });
      }
    }
  }
  return result;
}

function fmtMin(seconds: number): string {
  return `${Math.round(seconds / 60)} min`;
}

function getDaySteps(dayIdx: number, allSteps: WorkoutStep[]): WorkoutStep[] {
  return allSteps.filter(s => s.dayIndex === dayIdx);
}

function challengeIcon(_type: string) {
  return faMedal;
}

// ── Confetti ─────────────────────────────────────────────────────────────────

function Confetti() {
  const pieces = useMemo(() =>
    Array.from({ length: 70 }, (_, i) => ({
      id: i,
      left:     Math.random() * 100,
      delay:    Math.random() * 1.8,
      duration: 2 + Math.random() * 2.5,
      color: ['#ff6b6b','#ffd93d','#6bcb77','#4d96ff','#f4a261','#9b5de5','#ff9ff3'][i % 7],
      size:  6 + Math.random() * 8,
      round: Math.random() > 0.5,
    })), []);

  return (
    <div className="confetti-container" aria-hidden>
      {pieces.map(p => (
        <div key={p.id} className="confetti-piece" style={{
          left: `${p.left}%`,
          animationDelay: `${p.delay}s`,
          animationDuration: `${p.duration}s`,
          backgroundColor: p.color,
          width: `${p.size}px`,
          height: `${p.size}px`,
          borderRadius: p.round ? '50%' : '2px',
        }} />
      ))}
    </div>
  );
}

// ── Sport Breakdown ───────────────────────────────────────────────────────────

interface SportSegment {
  sport: string;
  label: string;
  pts: number;
  color: string;
}

function buildSegments(activities: Activity[]): SportSegment[] {
  const byType: Record<string, number> = {};
  let bonusPts = 0;
  let prPts = 0;

  for (const a of activities) {
    const base = Number(a.base_points) || 0;
    bonusPts += Number(a.challenge_bonus_points) || 0;
    prPts    += (Number(a.pr_bonus_points) || 0);
    if (base > 0) {
      byType[a.sport_type] = (byType[a.sport_type] ?? 0) + base;
    }
  }

  const segs: SportSegment[] = Object.entries(byType).map(([sport, pts]) => ({
    sport,
    label: getSportLabel(sport),
    pts,
    color: SPORT_COLORS[sport] ?? SPORT_COLORS['_other'],
  }));

  if (bonusPts > 0) segs.push({ sport: '_bonus', label: 'Bonus', pts: bonusPts, color: SPORT_COLORS['_bonus'] });
  if (prPts > 0)    segs.push({ sport: '_pr',    label: 'PRs',   pts: prPts,    color: SPORT_COLORS['_pr'] });

  return segs.sort((a, b) => b.pts - a.pts);
}

interface SportBreakdownProps {
  meActivities: Activity[];
  oppActivities: Activity[];
  meName: string;
  oppName: string;
  visible: boolean;
}

function SportBreakdown({ meActivities, oppActivities, meName, oppName, visible }: SportBreakdownProps) {
  const meSegs  = useMemo(() => buildSegments(meActivities),  [meActivities]);
  const oppSegs = useMemo(() => buildSegments(oppActivities), [oppActivities]);
  const meTotal  = meSegs.reduce((s, x) => s + x.pts, 0);
  const oppTotal = oppSegs.reduce((s, x) => s + x.pts, 0);
  const maxPts   = Math.max(meTotal, oppTotal, 1);

  const renderBars = (segs: SportSegment[], total: number, side: 'me' | 'opp') => {
    let cumPct = 0;
    return segs.map((seg, i) => {
      const pct   = (seg.pts / maxPts) * 100;
      const delay = visible ? i * 120 : 0;
      const bar = (
        <div
          key={seg.sport}
          className={`sb-bar-seg sb-bar-seg--${side}`}
          style={{
            width:                  visible ? `${pct}%` : '0%',
            backgroundColor:        seg.color,
            transitionDelay:        `${delay}ms`,
            transitionDuration:     '600ms',
          }}
          title={`${seg.label}: ${Math.round(seg.pts)} pts`}
        />
      );
      cumPct += pct;
      return bar;
    });
  };

  const renderLegend = (segs: SportSegment[], activities: Activity[]) =>
    segs.map(seg => (
      <div key={seg.sport} className="sb-legend-row">
        <span className="sb-legend-dot" style={{ background: seg.color }} />
        {seg.sport !== '_bonus' && seg.sport !== '_pr'
          ? <FontAwesomeIcon icon={getSportIcon(seg.sport)} className="sb-legend-icon" />
          : null}
        <span className="sb-legend-label">{seg.label}</span>
        <span className="sb-legend-pts">{Math.round(seg.pts)} pts</span>
      </div>
    ));

  return (
    <div className={`sport-breakdown${visible ? ' sport-breakdown--visible' : ''}`}>
      <h3 className="sb-title">Points by Sport</h3>
      <div className="sb-side">
        <div className="sb-side-name sb-side-name--me">{meName}</div>
        <div className="sb-bar-track">
          {renderBars(meSegs, meTotal, 'me')}
        </div>
        <div className="sb-legend">{renderLegend(meSegs, meActivities)}</div>
      </div>
      <div className="sb-divider" />
      <div className="sb-side">
        <div className="sb-side-name sb-side-name--opp">{oppName}</div>
        <div className="sb-bar-track">
          {renderBars(oppSegs, oppTotal, 'opp')}
        </div>
        <div className="sb-legend">{renderLegend(oppSegs, oppActivities)}</div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

interface Props { onBack: () => void; matchupId?: number; onNavigateToPlayerProfile?: (playerId: number) => void; }

export default function DuelScorePage({ onBack, matchupId, onNavigateToPlayerProfile }: Props) {
  const { token } = useAuth();
  const [phase,        setPhase]        = useState<Phase>('loading');
  const [duelData,     setDuelData]     = useState<DuelData | null>(null);
  const [weekChallenges, setWeekChallenges] = useState<Record<string, DailyChallenge>>({});
  const [challengeTip,   setChallengeTip]   = useState<{ x: number; y: number } | null>(null);  useScrollDismiss(challengeTip, setChallengeTip);  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);
  const [steps,        setSteps]        = useState<WorkoutStep[]>([]);
  const [finalComment, setFinalComment] = useState('');
  const [stepIndex,    setStepIndex]    = useState(-1);
  const [speed,        setSpeed]        = useState<1 | 2 | 5>(1);
  const [flyingOut,         setFlyingOut]         = useState(false);
  const [showBubble,        setShowBubble]        = useState(false);
  const [displayedStepIndex, setDisplayedStepIndex] = useState(-1);
  const [avatarFlash,       setAvatarFlash]       = useState<'me' | 'opp' | null>(null);
  const [bonusHighlight,    setBonusHighlight]    = useState(false);
  const [dayIntro,          setDayIntro]          = useState(false);
  const [myStats,      setMyStats]      = useState<PlayerStats | null | false>(null);
  const [oppStats,     setOppStats]     = useState<PlayerStats | null | false>(null);
  const [topTooltip,   setTopTooltip]   = useState<{ which: 'mine' | 'opp'; x: number; y: number } | null>(null);
  useScrollDismiss(topTooltip, setTopTooltip);
  const [barTip,       setBarTip]       = useState<{ x: number; y: number; seg: { key: string; color: string; title: string } } | null>(null);
  const [showItemReward, setShowItemReward] = useState(false);
  const [hasPendingReward, setHasPendingReward] = useState<boolean | null>(null);
  useScrollDismiss(barTip, setBarTip);

  const STEP_MS = Math.round(STEP_MS_BASE / speed);

  // ── Close challenge/bar tooltip on outside click (touch) ──────────────────
  useEffect(() => {
    if (!challengeTip && !barTip) return;
    if (!isTouch.current && !barTip) return;
    const handler = () => { setChallengeTip(null); setBarTip(null); };
    document.addEventListener('click', handler, { once: true });
    return () => document.removeEventListener('click', handler);
  }, [challengeTip, barTip]);

  // ── Data loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;
    api.get('/challenges/week', token).then((d: any) => {
      if (d?.challenges) setWeekChallenges(d.challenges);
    });
    api.get(matchupId ? `/duel/result/${matchupId}` : '/duel/result', token).then((data: any) => {
      if (data?.error) { setPhase('error'); return; }
      const d = data as DuelData;
      setDuelData(d);
      api.get(`/player/${d.me.id}/stats`, token).then((s: any) => {
        if (s?.wins !== undefined) setMyStats(s as PlayerStats); else setMyStats(false);
      }).catch(() => setMyStats(false));
      if (!d.opponent.is_bot) {
        api.get(`/player/${d.opponent.id}/stats`, token).then((s: any) => {
          if (s?.wins !== undefined) setOppStats(s as PlayerStats); else setOppStats(false);
        }).catch(() => setOppStats(false));
      } else {
        setOppStats(false);
      }

      const comments = generateAllComments(
        d.me.activities,
        d.opponent.activities,
        d.me.display_name,
        d.opponent.display_name,
      );
      setFinalComment(generateFinalComment(d.winner));
      setSteps(buildSteps(d.me.activities, d.opponent.activities, d.cycle.start_at_utc, comments));
      setPhase('ready');
    }).catch(() => setPhase('error'));
  }, [token]);

  // ── Animation timer ───────────────────────────────────────────────────────

  useEffect(() => {
    if (phase !== 'playing') return;
    setFlyingOut(false);
    setShowBubble(false);
    setAvatarFlash(null);
    setBonusHighlight(false);
    const isFirstOfDay = stepIndex === 0 ||
      (stepIndex > 0 && steps[stepIndex]?.dayIndex !== steps[stepIndex - 1]?.dayIndex);
    if (isFirstOfDay) {
      setDayIntro(true);
      // rest is driven by duel-day-intro-holder animationend
    } else {
      setDayIntro(false);
      const hasBonus = Number(steps[stepIndex]?.workout?.challenge_bonus_points ?? 0) > 0;
      setBonusHighlight(hasBonus);
      if (!hasBonus) setFlyingOut(true);
    }
  }, [phase, stepIndex, steps.length]);

  // ── Check for pending item reward (but DON'T auto-show) ──
  useEffect(() => {
    if (phase !== 'victory' || !token) return;
    api.get('/player/me/item-reward', token)
      .then((data: any) => {
        setHasPendingReward(!!data?.reward);
      })
      .catch(() => {});
  }, [phase, token]);

  // ── Controls ──────────────────────────────────────────────────────────────

  function play() {
    if (!steps.length) { setPhase('victory'); return; }
    if (stepIndex < 0) setStepIndex(0);
    setPhase('playing');
  }
  function pause() { setPhase('paused'); }

  function skipDay() {
    setBonusHighlight(false);
    setDayIntro(false);
    setAvatarFlash(null);
    setFlyingOut(false);
    setShowBubble(false);
    if (stepIndex < 0) { play(); return; }
    const curDay = steps[stepIndex]?.dayIndex ?? 0;
    // Credit all steps of the skipped day immediately
    let lastOfDay = stepIndex;
    for (let i = stepIndex + 1; i < steps.length; i++) {
      if (steps[i].dayIndex === curDay) lastOfDay = i;
      else break;
    }
    setDisplayedStepIndex(lastOfDay);
    const nextIdx = steps.findIndex((s, i) => i > stepIndex && s.dayIndex > curDay);
    if (nextIdx === -1) { setPhase('victory'); } else { setStepIndex(nextIdx); setPhase('playing'); }
  }

  function restart() { setStepIndex(-1); setPhase('ready'); setFlyingOut(false); setShowBubble(false); setDisplayedStepIndex(-1); setAvatarFlash(null); setBonusHighlight(false); setDayIntro(false); }

  function showTopTooltip(e: React.MouseEvent<Element>, which: 'mine' | 'opp') {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTopTooltip({ which, x: clampTooltipX(rect.left + rect.width / 2, 240, true), y: rect.bottom + 10 });
  }
  function hideTopTooltip() { setTopTooltip(null); }
  function toggleTopTooltip(e: React.MouseEvent<Element>, which: 'mine' | 'opp') {
    e.stopPropagation();
    if (topTooltip?.which === which) setTopTooltip(null);
    else showTopTooltip(e, which);
  }

  // ── Derived state ─────────────────────────────────────────────────────────

  const curStep      = stepIndex >= 0 && stepIndex < steps.length ? steps[stepIndex] : null;
  // Points update when the fly animation ends (via onAnimationEnd → displayedStepIndex), not before
  const displayedStep = displayedStepIndex >= 0 && displayedStepIndex < steps.length ? steps[displayedStepIndex] : null;
  const mePoints  = displayedStep?.mePointsAfter  ?? 0;
  const oppPoints = displayedStep?.oppPointsAfter ?? 0;
  const meChalPts  = displayedStep?.meChallengeAfter  ?? 0;
  const oppChalPts = displayedStep?.oppChallengeAfter ?? 0;
  const mePrPts    = displayedStep?.mePrAfter  ?? 0;
  const oppPrPts   = displayedStep?.oppPrAfter ?? 0;
  const meBasePts  = mePoints  - meChalPts  - mePrPts;
  const oppBasePts = oppPoints - oppChalPts - oppPrPts;
  const maxTotal  = duelData ? Math.max(duelData.me.total_points, duelData.opponent.total_points, 1) : 1;
  const meBarPct  = Math.min(100, Math.round((mePoints  / maxTotal) * 100));
  const oppBarPct = Math.min(100, Math.round((oppPoints / maxTotal) * 100));
  const curDayIdx = curStep?.dayIndex ?? (phase === 'victory' ? 6 : 0);
  const flyMs     = Math.round(STEP_MS * 0.35);
  const days         = duelData ? getDaysFromCycleStart(duelData.cycle.start_at_utc) : [];
  const curDay       = days[curDayIdx] ?? null;
  const dateKey      = curDay
    ? `${curDay.getFullYear()}-${String(curDay.getMonth() + 1).padStart(2, '0')}-${String(curDay.getDate()).padStart(2, '0')}`
    : '';
  const curChallenge = weekChallenges[dateKey] ?? null;

  // Per-sport segments for the live stacked bars
  const buildBarSegs = (player: 'me' | 'opp', src: WorkoutStep[]) => {
    const byType: Record<string, number> = {};
    let chalPts = 0, prPts = 0;
    for (const s of src) {
      if (s.player !== player) continue;
      const base = Number(s.workout.base_points) || 0;
      if (base > 0) byType[s.workout.sport_type] = (byType[s.workout.sport_type] ?? 0) + base;
      chalPts += Number(s.workout.challenge_bonus_points) || 0;
      prPts   += Number(s.workout.pr_bonus_points)        || 0;
    }
    const sorted = Object.entries(byType).sort((a, b) => b[1] - a[1]);
    const segs: { key: string; color: string; widthPct: number; leftPct: number; title: string }[] = [];
    let cursor = 0;
    for (const [sport, pts] of sorted) {
      const w = (pts / maxTotal) * 100;
      segs.push({ key: sport, color: SPORT_COLORS[sport] ?? SPORT_COLORS['_other'], widthPct: w, leftPct: cursor, title: `${getSportLabel(sport)}: ${Math.round(pts)} pts` });
      cursor += w;
    }
    if (chalPts > 0) {
      const w = (chalPts / maxTotal) * 100;
      segs.push({ key: '_bonus', color: SPORT_COLORS['_bonus'], widthPct: w, leftPct: cursor, title: `Bonus: ${Math.round(chalPts)} pts` });
      cursor += w;
    }
    if (prPts > 0) {
      const w = (prPts / maxTotal) * 100;
      segs.push({ key: '_pr', color: SPORT_COLORS['_pr'], widthPct: w, leftPct: cursor, title: `PRs: ${Math.round(prPts)} pts` });
    }
    return segs;
  };

  // During animation: only revealed steps; at victory: all steps of the week
  const barSteps = phase === 'victory' ? steps : steps.slice(0, displayedStepIndex + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const meBarSegs  = useMemo(() => buildBarSegs('me', barSteps),  [barSteps]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const oppBarSegs = useMemo(() => buildBarSegs('opp', barSteps), [barSteps]);

  // ── Loading / error ───────────────────────────────────────────────────────

  if (phase === 'loading') return (
    <div className="duel-score-page">
      <header className="duel-score-header">
        <button className="duel-score-back" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
        <h2 className="duel-score-title"><FontAwesomeIcon icon={faFlagCheckered} /> Duel Results</h2>
      </header>
      <div className="duel-score-loading">Loading duel results and generating comments…</div>
    </div>
  );

  if (phase === 'error' || !duelData) return (
    <div className="duel-score-page">
      <header className="duel-score-header">
        <button className="duel-score-back" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
        <h2 className="duel-score-title"><FontAwesomeIcon icon={faFlagCheckered} /> Duel Results</h2>
      </header>
      <div className="duel-score-loading">Duel results are not available yet.</div>
    </div>
  );

  const isDraw     = duelData.winner === 'draw';
  const iWin       = duelData.winner === 'me';
  const winnerData = isDraw ? null : (iWin ? duelData.me  : duelData.opponent);
  const loserData  = isDraw ? null : (iWin ? duelData.opponent : duelData.me);
  // Consolation earned this match (from DB Activity, not calculated)
  const myConsolationEarned = (!isDraw && !iWin && !duelData.opponent.is_bot)
    ? (duelData.me.consolation_points ?? 0)
    : 0;
  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="duel-score-page">
      <header className="dashboard-header">
        <div className="dashboard-header-nav">
          <button className="duel-score-back" onClick={onBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
          <div className="week-label">{duelData.cycle.week_label}</div>
        </div>
        <img src={logo} alt="NoWeakWeek" className="dashboard-logo" />
        <h1>NoWeakWeek</h1>
      </header>

      {/* Day progress dots */}
      <div className="day-progress">
        {DAY_NAMES.map((name, i) => {
          const hasAny = steps.some(s => s.dayIndex === i);
          const isPast = i < curDayIdx;
          const isCur  = i === curDayIdx && phase !== 'victory';
          return (
            <div key={i} className={['day-dot-wrap', isPast ? 'past' : '', isCur ? 'current' : '', !hasAny ? 'empty' : ''].filter(Boolean).join(' ')}>
              <div className="day-dot" />
              <span className="day-dot-label">{name.slice(0, 3)}</span>
            </div>
          );
        })}
      </div>

      {/* Arena */}
      <div className={`duel-arena${bonusHighlight ? ' bonus-active' : ''}`}>

        {/* My side (left) */}
        <div className="duel-arena-player duel-arena-player--me">
          <div className="duel-arena-actor">
            <div
              className={`duel-arena-avatar-wrap${avatarFlash === 'me' ? ' flash' : ''}`}
              onAnimationEnd={(e: React.AnimationEvent<HTMLDivElement>) => {
                if (e.animationName !== 'avatarFlash') return;
                setAvatarFlash(null);
                setShowBubble(true);
              }}
            >
              {duelData.me.avatar_url
                ? <img
                    src={duelData.me.avatar_url} alt={duelData.me.display_name} className="duel-arena-avatar"
                    onMouseEnter={(e) => showTopTooltip(e, 'mine')}
                    onMouseLeave={hideTopTooltip}
                    onClick={() => onNavigateToPlayerProfile?.(duelData.me.id)}
                    style={{ cursor: 'pointer' }}
                  />
                : <FontAwesomeIcon icon={faRobot} className="duel-arena-avatar-icon" />
              }
              {phase !== 'victory' && mePoints > oppPoints && oppPoints > 0 && <FontAwesomeIcon icon={faCrown} className="versus-crown" />}
              {phase === 'victory' && (iWin || isDraw) && (
                <FontAwesomeIcon icon={faTrophy} className={`duel-victory-trophy${isDraw ? ' duel-victory-trophy--draw' : ''}`} />
              )}
            </div>
            {curStep?.player === 'opp' && showBubble && curStep.comment && (
              <div key={`bme-${stepIndex}`} className="speech-bubble speech-bubble--left">
                {curStep.comment}
              </div>
            )}
          </div>
          <div className="duel-arena-name">{duelData.me.display_name}</div>
          <div className="duel-points-bar-section">
            <div className="duel-points-bar-wrap">
              {meBarSegs.map(seg => (
                <div key={seg.key}
                  className="duel-points-bar-fill duel-points-bar-fill--sport"
                  style={{ '--seg-pct': `${seg.widthPct}%`, backgroundColor: seg.color } as React.CSSProperties}
                  onClick={(e) => {
                    if (!isTouch.current) return;
                    e.stopPropagation();
                    const r = e.currentTarget.getBoundingClientRect();
                    setBarTip(barTip ? null : { x: r.left + r.width / 2, y: r.top - 8, seg });
                  }}
                  title={!isTouch.current ? seg.title : undefined}
                />
              ))}
            </div>
            <div className="sb-legend sb-legend--inline">
              {meBarSegs.map(seg => (
                <div key={seg.key} className="sb-legend-row">
                  <span className="sb-legend-dot" style={{ background: seg.color }} />
                  {seg.key !== '_bonus' && seg.key !== '_pr'
                    ? <FontAwesomeIcon icon={getSportIcon(seg.key)} className="sb-legend-icon" />
                    : null}
                  <span className="sb-legend-label">{seg.title.split(':')[0]}</span>
                  <span className="sb-legend-pts">{seg.title.split(': ')[1]}</span>
                </div>
              ))}
              <div className="sb-total-row"><span className="sb-total-value">{Math.round(mePoints)} pts</span></div>
            </div>
          </div>
        </div>

        {/* Center */}
        <div className="duel-arena-center">
          {phase === 'victory'
            ? <div className="duel-arena-day-label">Final Score</div>
            : <>
                <div className="calendar-day-header">
                  <span className="day-name">{DAY_NAMES[curDayIdx]}</span>
                  {curDay && <span className="day-date">{curDay.getDate()}.{curDay.getMonth() + 1}.</span>}
                </div>
                {curChallenge && (
                  <div
                    className="day-challenge"
                    onMouseEnter={(e) => { if (!isTouch.current) { const r = e.currentTarget.getBoundingClientRect(); setChallengeTip({ x: clampTooltipX(r.left, 220), y: r.bottom + 6 }); } }}
                    onMouseLeave={() => { if (!isTouch.current) setChallengeTip(null); }}
                    onClick={(e) => { e.stopPropagation(); if (challengeTip) { setChallengeTip(null); } else { const r = e.currentTarget.getBoundingClientRect(); setChallengeTip({ x: clampTooltipX(r.left, 220), y: r.bottom + 6 }); } }}
                  >
                    <span className="day-challenge-icon"><FontAwesomeIcon icon={challengeIcon(curChallenge.type)} /></span>
                    <span className="day-challenge-title">{curChallenge.title}</span>
                  </div>
                )}
              </>
          }

          {phase !== 'victory' && stepIndex < 0 && (
            <div className="duel-intro-info">
              <span className="duel-intro-count">{duelData.me.activities.length}</span>
              <span className="duel-intro-vs">VS</span>
              <span className="duel-intro-count">{duelData.opponent.activities.length}</span>
              <span className="duel-intro-sub">workouts this week</span>
            </div>
          )}

          {phase !== 'victory' && stepIndex >= 0 && (
            <div key={`day-${curDayIdx}`} className="duel-day-cal" style={{ '--fly-ms': `${flyMs}ms` } as React.CSSProperties}>
              {getDaySteps(curDayIdx, steps).map((step) => {
                const globalIdx = steps.indexOf(step);
                const isDone    = globalIdx < stepIndex;
                const isCurrent = globalIdx === stepIndex;
                const isFlying  = isCurrent && flyingOut;
                return (
                  <div
                    key={step.workout.id}
                    className={[
                      'duel-cal-item',
                      isDone    ? 'is-done'    : '',
                      isCurrent ? 'is-current' : '',
                      isFlying  ? (step.player === 'me' ? 'fly-left' : 'fly-right') : '',
                    ].filter(Boolean).join(' ')}
                    onAnimationEnd={isFlying ? (e: React.AnimationEvent<HTMLDivElement>) => {
                      if (e.animationName !== 'slotCollapse') return;
                      setAvatarFlash(step.player);
                      setDisplayedStepIndex(globalIdx);
                      // showBubble is set by avatarFlash animationend
                    } : undefined}
                  >
                    <ActivityChip
                      activity={step.workout}
                      variant={step.player === 'me' ? 'mine' : 'opponent'}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {phase === 'victory' && (
            <div className="duel-final-scores">
              <span className={`duel-final-pts${iWin ? ' duel-final-pts--winner' : ''}`}>
                {Math.round(duelData.me.total_points)}
              </span>
              <span className="duel-final-colon">:</span>
              <span className={`duel-final-pts${!iWin && !isDraw ? ' duel-final-pts--winner' : ''}`}>
                {Math.round(duelData.opponent.total_points)}
              </span>
            </div>
          )}
        </div>

        {/* Opponent side (right) */}
        <div className="duel-arena-player duel-arena-player--opp">
          <div className="duel-arena-actor">
            <div
              className={`duel-arena-avatar-wrap${avatarFlash === 'opp' ? ' flash' : ''}`}
              onAnimationEnd={(e: React.AnimationEvent<HTMLDivElement>) => {
                if (e.animationName !== 'avatarFlash') return;
                setAvatarFlash(null);
                setShowBubble(true);
              }}
            >
              {duelData.opponent.is_bot
                ? <FontAwesomeIcon icon={faRobot} className="duel-arena-avatar-icon" />
                : duelData.opponent.avatar_url
                  ? <img
                      src={duelData.opponent.avatar_url} alt={duelData.opponent.display_name} className="duel-arena-avatar"
                      onMouseEnter={(e) => showTopTooltip(e, 'opp')}
                      onMouseLeave={hideTopTooltip}
                      onClick={() => onNavigateToPlayerProfile?.(duelData.opponent.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  : <FontAwesomeIcon icon={faRobot} className="duel-arena-avatar-icon" />
              }
              {phase !== 'victory' && oppPoints > mePoints && <FontAwesomeIcon icon={faCrown} className="versus-crown" />}
              {phase === 'victory' && (!iWin || isDraw) && (
                <FontAwesomeIcon icon={faTrophy} className={`duel-victory-trophy${isDraw ? ' duel-victory-trophy--draw' : ''}`} />
              )}
            </div>
            {curStep?.player === 'me' && showBubble && curStep.comment && (
              <div key={`bopp-${stepIndex}`} className="speech-bubble speech-bubble--right">
                {curStep.comment}
              </div>
            )}
          </div>
          <div className="duel-arena-name">{duelData.opponent.display_name}</div>
          <div className="duel-points-bar-section">
            <div className="duel-points-bar-wrap">
              {oppBarSegs.map(seg => (
                <div key={seg.key}
                  className="duel-points-bar-fill duel-points-bar-fill--sport"
                  style={{ '--seg-pct': `${seg.widthPct}%`, backgroundColor: seg.color } as React.CSSProperties}
                  onClick={(e) => {
                    if (!isTouch.current) return;
                    e.stopPropagation();
                    const r = e.currentTarget.getBoundingClientRect();
                    setBarTip(barTip ? null : { x: r.left + r.width / 2, y: r.top - 8, seg });
                  }}
                  title={!isTouch.current ? seg.title : undefined}
                />
              ))}
            </div>
            <div className="sb-legend sb-legend--inline">
              {oppBarSegs.map(seg => (
                <div key={seg.key} className="sb-legend-row">
                  <span className="sb-legend-dot" style={{ background: seg.color }} />
                  {seg.key !== '_bonus' && seg.key !== '_pr'
                    ? <FontAwesomeIcon icon={getSportIcon(seg.key)} className="sb-legend-icon" />
                    : null}
                  <span className="sb-legend-label">{seg.title.split(':')[0]}</span>
                  <span className="sb-legend-pts">{seg.title.split(': ')[1]}</span>
                </div>
              ))}
              <div className="sb-total-row"><span className="sb-total-value">{Math.round(oppPoints)} pts</span></div>
            </div>
          </div>
        </div>

        {/* Invisible day-intro holder — 1.5s pause before first activity of each day */}
        {phase === 'playing' && dayIntro && (
          <div
            key={`dayintroholder-${stepIndex}`}
            className="duel-day-intro-holder"
            style={{ '--day-intro-ms': '1500ms' } as React.CSSProperties}
            onAnimationEnd={(e: React.AnimationEvent<HTMLDivElement>) => {
              if (e.animationName !== 'dayIntroHold') return;
              setDayIntro(false);
              const hasBonus = Number(steps[stepIndex]?.workout?.challenge_bonus_points ?? 0) > 0;
              if (hasBonus) setBonusHighlight(true);
              else setFlyingOut(true);
            }}
          />
        )}
        {/* Invisible bonus holder — drives bonus→fly transition via animationend */}
        {phase === 'playing' && bonusHighlight && (
          <div
            key={`bonusholder-${stepIndex}`}
            className="duel-bonus-holder"
            style={{ '--bonus-hold-ms': '900ms' } as React.CSSProperties}
            onAnimationEnd={(e: React.AnimationEvent<HTMLDivElement>) => {
              if (e.animationName !== 'bonusHold') return;
              setBonusHighlight(false);
              setFlyingOut(true);
            }}
          />
        )}
        {/* Invisible pause holder — drives timing via CSS bubbleHold animation */}
        {phase === 'playing' && showBubble && (
          <div
            key={`pauseholder-${stepIndex}`}
            className="duel-pause-holder"
            style={{ '--bubble-hold-ms': '2500ms' } as React.CSSProperties}
            onAnimationEnd={(e: React.AnimationEvent<HTMLDivElement>) => {
              if (e.animationName !== 'bubbleHold') return;
              setShowBubble(false);
              setFlyingOut(false);
              const next = stepIndex + 1;
              if (next >= steps.length) setPhase('victory');
              else setStepIndex(next);
            }}
          />
        )}
      </div>

      {/* Controls */}
      {phase !== 'victory' && (
        <div className="replay-controls">
          {phase === 'playing'
            ? <button className="replay-btn" onClick={pause}><FontAwesomeIcon icon={faPause} /></button>
            : <button className="replay-btn replay-btn--play" onClick={play}>
                <FontAwesomeIcon icon={faPlay} /> {stepIndex < 0 ? 'Start' : 'Continue'}
              </button>
          }
          <button className="replay-btn replay-btn--skip" onClick={skipDay} disabled={phase !== 'playing' && phase !== 'paused'}>
            <FontAwesomeIcon icon={faForwardStep} /> Skip Day
          </button>
        </div>
      )}

      {/* Victory overlay */}
      {phase === 'victory' && (
        <div className="duel-victory-overlay">
          <Confetti />
          <div className="duel-victory-content">
            {isDraw ? (
              <>
                <FontAwesomeIcon icon={faHandshake} className="duel-victory-icon duel-victory-icon--draw" />
                <h2 className="duel-victory-headline">It's a Draw!</h2>
                <p className="duel-victory-sub">An equal result — rematch next week!</p>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTrophy} className="duel-victory-icon" />
                <h2 className="duel-victory-headline">{winnerData?.display_name} wins!</h2>
              </>
            )}

            {/* Score grid – like duel-card versus-bar */}
            <div className="duel-victory-score-grid">
              <div className="duel-victory-score-cell duel-victory-score-cell--me">
                <span className="duel-victory-score-name">{duelData.me.display_name}</span>
                <span className="duel-victory-score-pts">{Math.round(duelData.me.total_points)}</span>
                <span className="duel-victory-score-label">pts</span>
              </div>
              <span className="duel-victory-score-vs">vs</span>
              <div className="duel-victory-score-cell duel-victory-score-cell--opp">
                <span className="duel-victory-score-name">{duelData.opponent.display_name}</span>
                <span className="duel-victory-score-pts">{Math.round(duelData.opponent.total_points)}</span>
                <span className="duel-victory-score-label">pts</span>
              </div>
            </div>

            {/* ELO + Rank – compact grid */}
            <div className="duel-victory-stats-grid">
              <div className="duel-victory-stat-cell duel-victory-stat-cell--me">
                <span className="duel-victory-stat-name">{duelData.me.display_name}</span>
                <span className="duel-victory-stat-elo">
                  {duelData.elo?.me?.before != null && duelData.elo?.me?.after != null ? (
                    <>
                      {duelData.elo.me.before} → {duelData.elo.me.after}
                      <span className={`duel-victory-stat-delta ${(duelData.elo.me.change ?? 0) >= 0 ? 'pos' : 'neg'}`}>
                        {(duelData.elo.me.change ?? 0) >= 0 ? '+' : ''}{duelData.elo.me.change}
                      </span>
                    </>
                  ) : (
                    <span className="neutral">{duelData.me.elo_rating ?? '?'}</span>
                  )}
                </span>
                {duelData.me.rank != null && (
                  <span className="duel-victory-stat-rank">
                    🔥 #{duelData.me.rank}
                    {duelData.elo?.me?.change != null && (
                      <FontAwesomeIcon
                        icon={duelData.elo.me.change > 0 ? faArrowUp : duelData.elo.me.change < 0 ? faArrowDown : faEquals}
                        className={`duel-victory-stat-rank-arrow ${duelData.elo.me.change > 0 ? 'pos' : duelData.elo.me.change < 0 ? 'neg' : 'neutral'}`}
                      />
                    )}
                  </span>
                )}
              </div>
              <div className="duel-victory-stat-cell duel-victory-stat-cell--opp">
                <span className="duel-victory-stat-name">{duelData.opponent.display_name}</span>
                <span className="duel-victory-stat-elo">
                  {duelData.elo?.opp?.before != null && duelData.elo?.opp?.after != null ? (
                    <>
                      {duelData.elo.opp.before} → {duelData.elo.opp.after}
                      <span className={`duel-victory-stat-delta ${(duelData.elo.opp.change ?? 0) >= 0 ? 'pos' : 'neg'}`}>
                        {(duelData.elo.opp.change ?? 0) >= 0 ? '+' : ''}{duelData.elo.opp.change}
                      </span>
                    </>
                  ) : (
                    <span className="neutral">{duelData.opponent.elo_rating ?? '?'}</span>
                  )}
                </span>
                {duelData.opponent.rank != null && (
                  <span className="duel-victory-stat-rank">
                    🔥 #{duelData.opponent.rank}
                    {duelData.elo?.opp?.change != null && (
                      <FontAwesomeIcon
                        icon={duelData.elo.opp.change > 0 ? faArrowUp : duelData.elo.opp.change < 0 ? faArrowDown : faEquals}
                        className={`duel-victory-stat-rank-arrow ${duelData.elo.opp.change > 0 ? 'pos' : duelData.elo.opp.change < 0 ? 'neg' : 'neutral'}`}
                      />
                    )}
                  </span>
                )}
              </div>
            </div>

            {finalComment && (
              <div className="duel-victory-quote">
                <span className="duel-victory-speaker">
                  {(isDraw ? duelData.opponent.display_name : loserData?.display_name) ?? ''}:{' '}
                </span>
                "{finalComment}"
              </div>
            )}

            {myConsolationEarned > 0 && (
              <div className="duel-victory-consolation">
                <div className="duel-victory-consolation-body">
                  <span className="duel-victory-consolation-pts">+{Math.round(myConsolationEarned)} Consolation</span>
                  <span className="duel-victory-consolation-hint">
                    You take 20% of your points into the next match – they keep stacking until you win.
                  </span>
                </div>
              </div>
            )}

            {/* Badge-Erhalt / Tier-Upgrade – Icons mit Tooltip wie BadgeBar */}
            {((duelData.badges?.me?.new?.length ?? 0) > 0 || (duelData.badges?.me?.upgraded?.length ?? 0) > 0) && (
              <VictoryBadges badges={[...(duelData.badges?.me?.new ?? []), ...(duelData.badges?.me?.upgraded ?? [])]} />
            )}

            {/* Betting result – ausgeblendet
            {duelData.bet && (
              <div className={`duel-victory-bet ${duelData.bet.net_points >= 0 ? 'duel-victory-bet--won' : 'duel-victory-bet--lost'}`}>
                <div className="duel-victory-bet-icon">💰</div>
                <div className="duel-victory-bet-body">
                  <span className="duel-victory-bet-pts">
                    {duelData.bet.net_points >= 0 ? '+' : ''}{duelData.bet.net_points} Betting
                  </span>
                  <span className="duel-victory-bet-hint">
                    {duelData.bet.won} correct, {duelData.bet.lost} wrong
                    {duelData.bet.draw > 0 ? `, ${duelData.bet.draw} draw` : ''}
                  </span>
                </div>
              </div>
            )}
            */}

            <div className="duel-victory-actions">
              <button className="replay-btn replay-btn--play" onClick={restart}>
                <FontAwesomeIcon icon={faRotateLeft} /> Watch Again
              </button>
              <button className="replay-btn replay-btn--reward" onClick={() => setShowItemReward(true)}>
                Item Reward
              </button>
              <button className="replay-btn" onClick={() => {
                if (hasPendingReward) {
                  setShowItemReward(true);
                } else {
                  // Pending Items + Badges sichtbar machen, dann zurück
                  if (token) {
                    api.post('/player/me/claim-result', {}, token).then(() => onBack()).catch(() => onBack());
                  } else {
                    onBack();
                  }
                }
              }}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Reward Modal */}
      {showItemReward && (
        <ItemRewardModal
          onClaimed={() => {
            setShowItemReward(false);
            setHasPendingReward(false);
            // Pending Items + Badges sichtbar machen
            if (token) {
              api.post('/player/me/claim-result', {}, token).catch(() => {});
            }
          }}
          onDismiss={() => setShowItemReward(false)}
        />
      )}

      {challengeTip && curChallenge && (
        <div
          className="app-tooltip day-challenge-tip"
          style={{ position: 'fixed', top: challengeTip.y, left: challengeTip.x, zIndex: 9999 }}
          onClick={(e) => { e.stopPropagation(); setChallengeTip(null); }}
        >
          {curChallenge.description}
        </div>
      )}

      {barTip && (
        <div
          className="duel-bar-tooltip"
          style={{ position: 'fixed', top: barTip.y, left: barTip.x, transform: 'translateX(-50%) translateY(-100%)', zIndex: 9999, pointerEvents: 'none' }}
        >
          <span className="sb-legend-dot" style={{ background: barTip.seg.color }} />
          {barTip.seg.key !== '_bonus' && barTip.seg.key !== '_pr'
            ? <FontAwesomeIcon icon={getSportIcon(barTip.seg.key)} className="sb-legend-icon" />
            : null}
          <span className="sb-legend-label">{barTip.seg.title.split(':')[0]}</span>
          <span className="sb-legend-pts">{barTip.seg.title.split(': ')[1]}</span>
        </div>
      )}

      {topTooltip && (
        <div style={{ position: 'fixed', top: topTooltip.y, left: topTooltip.x, transform: 'translateX(-50%)', zIndex: 9999, pointerEvents: 'none' }}>
          {topTooltip.which === 'mine'
            ? <PlayerStatsTooltip stats={myStats} name={duelData.me.display_name} isMine={true} />
            : <PlayerStatsTooltip stats={oppStats} name={duelData.opponent.display_name} isMine={false} />
          }
        </div>
      )}
    </div>
  );
}

